/**
 * Section Analytics Clients
 * Alimentée par orgStatsDaily (customer KPIs agrégés) – zéro scan orders.
 */

import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Users, UserPlus, UserCheck, UserX, TrendingUp, DollarSign, HelpCircle, ShoppingCart } from 'lucide-react';
import { ChartTheme, CustomerKPIs } from './types';
import { KPICard, KPICardGrid } from './KPICard';
import { getBaseChartOptions, formatCurrency, formatDate } from './chartUtils';
import { Tooltip } from '@mui/material';
import { formatPercent } from '../customers';
import { useTranslation } from 'react-i18next';

interface CustomerSectionProps {
    customerData: CustomerKPIs;
    theme: ChartTheme;
    period: number;
    /** Inactifs (source: customerOrgs, cohérent avec la page Clients) */
    inactiveCount?: number;
    /** Total clients orga (pour le % inactifs) */
    totalCustomersFromOrgs?: number;
}

export const CustomerSection: React.FC<CustomerSectionProps> = ({ customerData, theme, period, inactiveCount = 0, totalCustomersFromOrgs }) => {
    const { t } = useTranslation();
    const baseOptions = getBaseChartOptions(theme);

    const totalCustomers = customerData.uniqueCustomers;
    const newCustomers = customerData.customersByLevel.occasionnel;
    const existingCustomersFromData = customerData.customersByLevel.regulier;
    const inactiveCustomers = inactiveCount;
    // Dénominateur unique pour des % cohérents (total clients uniques orga)
    const denominator = totalCustomersFromOrgs != null && totalCustomersFromOrgs > 0 ? totalCustomersFromOrgs : totalCustomers;
    // Existants actifs dérivés pour que Nouveaux + Existants actifs + Inactifs = 100%
    const existingActiveDisplay = denominator === totalCustomersFromOrgs
        ? Math.max(0, totalCustomersFromOrgs - newCustomers - inactiveCustomers)
        : existingCustomersFromData;
    const totalRevenue = customerData.totalRevenue ?? 0;
    const totalOrders = customerData.totalOrders ?? 0;
    const newRevenue = customerData.levelRevenueDistribution.find((x) => x.level === 'Nouveau')?.revenue ?? 0;
    const existingRevenue = customerData.levelRevenueDistribution.find((x) => x.level === 'Existant')?.revenue ?? 0;
    const newCustomersPercent = denominator > 0 ? (newCustomers / denominator) * 100 : 0;
    const existingCustomersPercent = denominator > 0 ? (existingActiveDisplay / denominator) * 100 : 0;
    const inactiveCustomersPercent = denominator > 0 ? (inactiveCustomers / denominator) * 100 : 0;
    const newRevenuePercent = totalRevenue > 0 ? (newRevenue / totalRevenue) * 100 : 0;
    const existingRevenuePercent = totalRevenue > 0 ? (existingRevenue / totalRevenue) * 100 : 0;

    const timeSeriesData = customerData.customerTimeSeries ?? [];
    const colors = { new: '#10B981', existing: '#3B82F6', inactive: '#9CA3AF' };

    const customerEvolutionOptions: Highcharts.Options = {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'area', height: 300 },
        title: { ...baseOptions.title, text: t('admin.dashboard.analytics.customer.evolutionTitle') },
        subtitle: {
            text: t('admin.dashboard.analytics.customer.evolutionSubtitle', { count: period }),
            style: { color: theme.secondaryColor, fontSize: '12px' },
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: timeSeriesData.map((d) => formatDate(d.date)),
            labels: { ...(baseOptions.xAxis as Highcharts.XAxisOptions)?.labels, step: Math.ceil(Math.max(1, timeSeriesData.length) / 10) },
        },
        yAxis: { ...baseOptions.yAxis, title: { text: t('admin.dashboard.analytics.customer.clients'), style: { color: theme.secondaryColor } } },
        tooltip: { ...baseOptions.tooltip, shared: true },
        plotOptions: { area: { stacking: 'normal', lineWidth: 2, marker: { enabled: false }, fillOpacity: 0.3 } },
        series: [
            { name: t('admin.dashboard.analytics.customer.newClients'), type: 'area', data: timeSeriesData.map((d) => d.newCustomers), color: colors.new },
            { name: t('admin.dashboard.analytics.customer.existingClients'), type: 'area', data: timeSeriesData.map((d) => d.existingCustomers), color: colors.existing },
        ],
    };

    const revenueByTypeOptions: Highcharts.Options = {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'column', height: 300 },
        title: { ...baseOptions.title, text: t('admin.dashboard.analytics.customer.revenueByType') },
        xAxis: {
            ...baseOptions.xAxis,
            categories: timeSeriesData.map((d) => formatDate(d.date)),
            labels: { ...(baseOptions.xAxis as Highcharts.XAxisOptions)?.labels, step: Math.ceil(Math.max(1, timeSeriesData.length) / 10) },
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: `${t('admin.dashboard.analytics.customer.revenue')} (€)`, style: { color: theme.secondaryColor } },
            labels: { formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) { return formatCurrency(Number(this.value)); }, style: { color: theme.secondaryColor } },
        },
        tooltip: {
            ...baseOptions.tooltip,
            shared: true,
            formatter: function (this: Highcharts.Point) {
                const points = (this as any).points;
                if (!points) return '';
                let s = `<b>${(this as any).x}</b><br/>`;
                points.forEach((point: any) => { s += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatCurrency(point.y || 0)}</b><br/>`; });
                return s;
            },
        },
        plotOptions: { column: { stacking: 'normal', borderRadius: 4 } },
        series: [
            { name: t('admin.dashboard.analytics.customer.newClients'), type: 'column', data: timeSeriesData.map((d) => d.newRevenue), color: colors.new },
            { name: t('admin.dashboard.analytics.customer.existingClients'), type: 'column', data: timeSeriesData.map((d) => d.existingRevenue), color: colors.existing },
        ],
    };

    const customerDistributionOptions: Highcharts.Options = {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'pie', height: 280 },
        title: { ...baseOptions.title, text: t('admin.dashboard.analytics.customer.distribution') },
        tooltip: { ...baseOptions.tooltip, pointFormat: `<b>{point.percentage:.1f}%</b> ({point.y} ${t('admin.dashboard.analytics.customer.clients').toLowerCase()})` },
        plotOptions: { pie: { innerSize: '60%', dataLabels: { enabled: true, format: '<b>{point.name}</b>', style: { color: theme.textColor, fontSize: '11px' }, distance: 10 }, showInLegend: true } },
        series: [{
            name: t('admin.dashboard.analytics.customer.clients'),
            type: 'pie',
            data: [
                { name: t('admin.dashboard.analytics.customer.statusDefinition.new'), y: newCustomers, color: colors.new },
                { name: t('admin.dashboard.analytics.customer.statusDefinition.existing'), y: existingActiveDisplay, color: colors.existing },
                ...(inactiveCustomers > 0 ? [{ name: t('admin.dashboard.analytics.customer.statusDefinition.inactive'), y: inactiveCustomers, color: colors.inactive }] : []),
            ].filter((d: any) => d.y > 0),
        }],
    };

    const revenueDistributionOptions: Highcharts.Options = {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'pie', height: 280 },
        title: { ...baseOptions.title, text: t('admin.dashboard.analytics.customer.revenueDistribution') },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function (this: Highcharts.Point) {
                const point = this as any;
                return `<b>${point.name}</b><br/>${formatCurrency(point.y)}<br/>${point.percentage?.toFixed(1) ?? 0}% ${t('admin.dashboard.analytics.customer.ofRevenue')}`;
            },
        },
        plotOptions: { pie: { innerSize: '60%', dataLabels: { enabled: true, format: '<b>{point.name}</b>', style: { color: theme.textColor, fontSize: '11px' }, distance: 10 }, showInLegend: true } },
        series: [{
            name: t('admin.dashboard.analytics.customer.revenue'),
            type: 'pie',
            data: [
                { name: t('admin.dashboard.analytics.customer.statusDefinition.new'), y: newRevenue, color: colors.new },
                { name: t('admin.dashboard.analytics.customer.statusDefinition.existing'), y: existingRevenue, color: colors.existing },
            ].filter((d: any) => d.y > 0),
        }],
    };

    const topCustomers = customerData.topCustomers ?? [];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-foreground">{t('admin.dashboard.analytics.customer.title')}</h2>
                <Tooltip
                    title={
                        <div className="p-2 max-w-sm">
                            <p className="font-semibold mb-2">{t('admin.dashboard.analytics.customer.statusDefinition.title')}</p>
                            <ul className="text-xs space-y-1">
                                <li><span className="font-medium text-green-400">{t('admin.dashboard.analytics.customer.statusDefinition.new')}</span> : {t('admin.dashboard.analytics.customer.statusDefinition.newDesc')}</li>
                                <li><span className="font-medium text-blue-400">{t('admin.dashboard.analytics.customer.statusDefinition.existing')}</span> : {t('admin.dashboard.analytics.customer.statusDefinition.existingDesc')}</li>
                                <li><span className="font-medium text-gray-400">{t('admin.dashboard.analytics.customer.statusDefinition.inactive')}</span> : {t('admin.dashboard.analytics.customer.statusDefinition.inactiveDesc')}</li>
                            </ul>
                        </div>
                    }
                    arrow
                >
                    <button className="p-2 hover:bg-background-tertiary rounded-lg transition-colors">
                        <HelpCircle className="h-5 w-5 text-foreground-secondary" />
                    </button>
                </Tooltip>
            </div>

            <KPICardGrid columns={6}>
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.totalCustomers')} value={totalCustomers} icon={<Users className="h-5 w-5" />} tooltip={t('admin.dashboard.analytics.customer.kpis.totalCustomersTooltip')} />
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.new')} value={`${newCustomers} (${formatPercent(newCustomersPercent)})`} icon={<UserPlus className="h-5 w-5" />} color="success" tooltip={t('admin.dashboard.analytics.customer.kpis.newTooltip')} />
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.existingActive')} value={`${existingActiveDisplay} (${formatPercent(existingCustomersPercent)})`} icon={<UserCheck className="h-5 w-5" />} color="primary" tooltip={t('admin.dashboard.analytics.customer.kpis.existingActiveTooltip')} />
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.inactive')} value={`${inactiveCustomers} (${formatPercent(inactiveCustomersPercent)})`} icon={<UserX className="h-5 w-5" />} color="info" tooltip={t('admin.dashboard.analytics.customer.kpis.inactiveTooltip')} />
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.periodOrders')} value={totalOrders} icon={<ShoppingCart className="h-5 w-5" />} tooltip={t('admin.dashboard.analytics.customer.kpis.periodOrdersTooltip')} />
                <KPICard title={t('admin.dashboard.analytics.customer.kpis.periodRevenue')} value={formatCurrency(totalRevenue)} icon={<DollarSign className="h-5 w-5" />} color="success" tooltip={t('admin.dashboard.analytics.customer.kpis.periodRevenueTooltip')} />
            </KPICardGrid>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-surface rounded-xl p-3 border border-border">
                    <HighchartsReact highcharts={Highcharts} options={customerEvolutionOptions} />
                </div>
                <div className="bg-surface rounded-xl p-3 border border-border">
                    <HighchartsReact highcharts={Highcharts} options={revenueByTypeOptions} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-surface rounded-xl p-3 border border-border">
                    <HighchartsReact highcharts={Highcharts} options={customerDistributionOptions} />
                </div>
                <div className="bg-surface rounded-xl p-3 border border-border">
                    <HighchartsReact highcharts={Highcharts} options={revenueDistributionOptions} />
                </div>
            </div>

            {topCustomers.length > 0 && (
                <div className="bg-surface rounded-xl p-5 border border-border">
                    <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" />
                        {t('admin.dashboard.analytics.customer.topCustomers.title')}
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left text-xs font-medium text-foreground-secondary py-2 px-3">{t('admin.dashboard.analytics.customer.topCustomers.client')}</th>
                                    <th className="text-center text-xs font-medium text-foreground-secondary py-2 px-3">{t('admin.dashboard.analytics.customer.topCustomers.status')}</th>
                                    <th className="text-right text-xs font-medium text-foreground-secondary py-2 px-3">{t('admin.dashboard.analytics.customer.topCustomers.orders')}</th>
                                    <th className="text-right text-xs font-medium text-foreground-secondary py-2 px-3">{t('admin.dashboard.analytics.customer.topCustomers.revenue')}</th>
                                    <th className="text-right text-xs font-medium text-foreground-secondary py-2 px-3">{t('admin.dashboard.analytics.customer.topCustomers.averageCart')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topCustomers.map((customer, index) => (
                                    <tr key={customer.email} className="border-b border-border last:border-0 hover:bg-background-tertiary">
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-bold text-foreground-secondary w-5">{index + 1}</span>
                                                <div>
                                                    <div className="font-medium text-foreground text-sm">{customer.name}</div>
                                                    <div className="text-xs text-foreground-secondary">{customer.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-3 text-center">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${customer.level === 'occasionnel' ? 'bg-success-10 text-success' : 'bg-primary-10 text-primary'}`}>
                                                {customer.level === 'occasionnel' ? t('admin.dashboard.analytics.customer.topCustomers.new') : t('admin.dashboard.analytics.customer.topCustomers.existing')}
                                            </span>
                                        </td>
                                        <td className="py-3 px-3 text-right text-sm font-medium text-foreground">{customer.totalOrders}</td>
                                        <td className="py-3 px-3 text-right text-sm font-bold text-success">{formatCurrency(customer.totalRevenue)}</td>
                                        <td className="py-3 px-3 text-right text-sm text-foreground-secondary">{formatCurrency(customer.averageCart)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="bg-gradient-to-r from-primary-10 to-transparent rounded-xl p-5 border border-primary-20">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-20 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-foreground mb-1">{t('admin.dashboard.analytics.customer.insights.title')}</h4>
                        <p className="text-sm text-foreground-secondary">
                            {existingRevenuePercent > 50 ? (
                                <span dangerouslySetInnerHTML={{ __html: t('admin.dashboard.analytics.customer.insights.strongBase', { percent: formatPercent(existingRevenuePercent) }) }} />
                            ) : newRevenuePercent > 50 ? (
                                <span dangerouslySetInnerHTML={{ __html: t('admin.dashboard.analytics.customer.insights.goodAcquisition', { percent: formatPercent(newRevenuePercent) }) }} />
                            ) : (
                                <span dangerouslySetInnerHTML={{ __html: t('admin.dashboard.analytics.customer.insights.balanced', { newPercent: formatPercent(newRevenuePercent), existingPercent: formatPercent(existingRevenuePercent) }) }} />
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerSection;
